import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaWpforms } from "react-icons/fa";
import { HiArrowUpTray, HiPaperAirplane } from "react-icons/hi2";
import { apiClient } from "../services/api";
import { lawyerInfoService } from "../services/lawyerInfoService";
import { RepresentativeInfo } from "../types/lawyer";
import DynamicEducationTable from "./DynamicEducationTable";
import DynamicWorkHistoryTable from "./DynamicWorkHistoryTable";
import DynamicFamilyMembersTable from "./DynamicFamilyMembersTable";
import DynamicTravelHistoryTable from "./DynamicTravelHistoryTable";
import DynamicAddressHistoryTable from "./DynamicAddressHistoryTable";
import DynamicFamilyDetailsTable from "./DynamicFamilyDetailsTable";
import DynamicAddressTable from "./DynamicAddressTable";
import DynamicTelephoneTable from "./DynamicTelephoneTable";
import DynamicMarriageInformationTable from "./DynamicMarriageInformationTable";
import DynamicNationalIdentityTable from "./DynamicNationalIdentityTable";
import DynamicPassportDetailsTable from "./DynamicPassportDetailsTable";
import DynamicUsaPrCardTable from "./DynamicUsaPrCardTable";
import DynamicOtherNamesTable from "./DynamicOtherNamesTable";
import DynamicPreviousCountriesTable from "./DynamicPreviousCountriesTable";
import DynamicApplicationCountryTable from "./DynamicApplicationCountryTable";
import DynamicPersonsToVisitTable from "./DynamicPersonsToVisitTable";
import IntakeFormDocumentUpload from "./IntakeFormDocumentUpload";
import "./PublicIntakeForm.css";

interface FormField {
  id: string;
  label: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "date"
    | "textarea"
    | "dropdown"
    | "radio"
    | "file"
    | "group"
    | "education-table"
    | "work-history-table"
    | "family-members-table"
    | "travel-history-table"
    | "address-history-table"
    | "family-details-table"
    | "address-table"
    | "telephone-table"
    | "marriage-information-table"
    | "national-identity-table"
    | "passport-details-table"
    | "usa-pr-card-table"
    | "other-names-table"
    | "previous-countries-table"
    | "application-country-table"
    | "persons-to-visit-table";
  required: boolean;
  options?: string[];
  placeholder?: string;
  fields?: FormField[]; // For group fields
}

interface IntakeForm {
  id: string;
  title: string;
  caseType: string;
  fields: FormField[];
  formId: string;
  lawyerId: string;
}

interface PublicIntakeFormProps {
  formId: string;
  initialFormData?: any;
  onFormChange?: (fieldId: string, value: any) => void;
}

const PublicIntakeForm: React.FC<PublicIntakeFormProps> = ({
  formId,
  initialFormData,
  onFormChange,
}) => {
  const [form, setForm] = useState<IntakeForm | null>(null);
  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initialFormData || {}
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [lawyerInfo, setLawyerInfo] = useState<RepresentativeInfo | null>(null);
  const [formSessionId, setFormSessionId] = useState<string | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"form" | "documents">("form");
  const [formProgress, setFormProgress] = useState(0);

  // Calculate form progress based on all fields
  const calculateProgress = () => {
    const allFields = form?.fields || [];
    const filledFields = allFields.filter((field) => {
      const value = formData[field.id];
      return value && value.toString().trim() !== "";
    });
    return allFields.length > 0
      ? Math.round((filledFields.length / allFields.length) * 100)
      : 0;
  };

  // Check if essential fields are filled for submit button activation
  const areEssentialFieldsFilled = () => {
    const essentialFields = ["surname", "givenName", "email"];
    return essentialFields.every((fieldId) => {
      const value = formData[fieldId];
      return value && value.toString().trim() !== "";
    });
  };

  const loadForm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔍 PublicIntakeForm: Loading form with formId:", formId);

      if (!formId) {
        console.log("❌ PublicIntakeForm: No formId provided");
        setError("Form ID is required");
        return;
      }

      console.log("📡 PublicIntakeForm: Calling getPublicIntakeForm...");
      const response = await apiClient.getPublicIntakeForm(formId);
      console.log("✅ PublicIntakeForm: Form loaded:", response);
      console.log("🔍 Form fields:", response.fields);
      setForm(response);

      // Create the complete form structure
      const completeFormData: { [key: string]: any } = {};
      response.fields.forEach((field: FormField) => {
        if (field.type === "education-table") {
          completeFormData[field.id] = {
            hasEducation: "No",
            educationEntries: [],
          };
        } else if (field.type === "work-history-table") {
          completeFormData[field.id] = {};
        } else if (field.type === "family-members-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "travel-history-table") {
          completeFormData[field.id] = {};
        } else if (field.type === "address-history-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "family-details-table") {
          completeFormData[field.id] = {};
        } else if (field.type === "address-table") {
          completeFormData[field.id] = {
            mailingAddress: {
              id: "mailing_1",
              poBox: "",
              streetNumber: "",
              streetName: "",
              aptNumber: "",
              city: "",
              province: "",
              country: "",
              postalCode: "",
              district: "",
            },
            residentialSameAsMailing: true,
            residentialAddressYes: "Y",
            residentialAddressNo: "N",
            residentialAddress: undefined,
          };
        } else if (field.type === "telephone-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "marriage-information-table") {
          completeFormData[field.id] = {};
        } else if (field.type === "national-identity-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "passport-details-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "usa-pr-card-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "other-names-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "previous-countries-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "application-country-table") {
          completeFormData[field.id] = [];
        } else if (field.type === "persons-to-visit-table") {
          completeFormData[field.id] = [];
        } else {
          completeFormData[field.id] = "";
        }
      });

      // Check if there's a form code in URL parameters to load saved data
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code) {
        try {
          console.log("🔍 Loading saved form data with code:", code);
          const savedFormData = await apiClient.loadFormByCode(code);
          console.log("✅ Loaded saved form data:", savedFormData);

          // Set form session ID from the loaded session data for document uploads
          if (savedFormData.session && savedFormData.session.id) {
            setFormSessionId(savedFormData.session.id);
            console.log(
              "✅ Set form session ID for document uploads:",
              savedFormData.session.id
            );
          }

          if (savedFormData.session && savedFormData.session.formData) {
            // Merge saved data with the complete form structure
            Object.keys(savedFormData.session.formData).forEach((key) => {
              if (completeFormData.hasOwnProperty(key)) {
                let value = savedFormData.session.formData[key];

                // Transform nested structures back to flat for form display
                if (key === "marriageInformation" && value) {
                  value = transformMarriageDataToFlat(value);
                } else if (key === "applicationCountryDetails" && value) {
                  value = transformApplicationCountryToFlat(value);
                } else if (key === "nationalIdentityDetails" && value) {
                  value = transformNationalIdentityToFlat(value);
                }

                completeFormData[key] = value;
              }
            });
            console.log("✅ Merged saved form data with form structure");
          }
        } catch (savedDataError) {
          console.log(
            "ℹ️ No saved form data found or error loading:",
            savedDataError
          );
          // Continue with empty form data if no saved data exists
        }
      }

      // Also check for existing client summary data for this form
      try {
        console.log("🔍 Checking for latest client summary data...");
        const existingSummary = await apiClient.getExistingClientSummary(
          response.lawyerId,
          response.formId
        );
        console.log("✅ Latest client summary data:", existingSummary);

        if (
          existingSummary &&
          existingSummary.data &&
          existingSummary.data.case_details
        ) {
          // Merge existing case_details data with the complete form structure
          Object.keys(existingSummary.data.case_details).forEach((key) => {
            if (completeFormData.hasOwnProperty(key)) {
              let value = existingSummary.data.case_details[key];

              // Transform nested structures back to flat for form display
              if (key === "marriageInformation" && value) {
                value = transformMarriageDataToFlat(value);
              } else if (key === "applicationCountryDetails" && value) {
                value = transformApplicationCountryToFlat(value);
              } else if (key === "nationalIdentityDetails" && value) {
                value = transformNationalIdentityToFlat(value);
              }

              completeFormData[key] = value;
            }
          });
          console.log(
            "✅ Merged latest client summary data with form structure"
          );
          console.log(
            "📊 Loaded fields:",
            Object.keys(existingSummary.data.case_details).filter(
              (key) =>
                completeFormData.hasOwnProperty(key) &&
                existingSummary.data.case_details[key]
            )
          );
        } else {
          console.log("ℹ️ No case_details found in latest client summary");
        }
      } catch (summaryError) {
        console.log(
          "ℹ️ No existing client summary found or error loading:",
          summaryError
        );
        // Continue with empty form data if no existing summary exists
      }

      setFormData(completeFormData);

      // Load lawyer information for the form creator
      try {
        console.log("🔍 Loading lawyer info for lawyerId:", response.lawyerId);

        if (!response.lawyerId) {
          console.log("❌ No lawyerId found in response");
          return;
        }

        const { data, error } = await lawyerInfoService.getLawyerInfoForForms(
          response.lawyerId
        );
        console.log("🔍 Lawyer info response:", { data, error });

        if (error) {
          console.log("❌ Error fetching lawyer info:", error);
          return;
        }

        if (data) {
          console.log("✅ Setting lawyer info:", data);
          setLawyerInfo(data);
        } else {
          console.log("❌ No lawyer info data found");
        }
      } catch (lawyerError) {
        console.log("❌ Exception loading lawyer info:", lawyerError);
        // Don't fail the form load if lawyer info can't be loaded
      }
    } catch (error) {
      console.error("Error loading form:", error);
      setError("Failed to load form. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [formId]); // Only depend on formId for now

  useEffect(() => {
    if (formId) {
      loadForm();
    }
  }, [formId, loadForm]); // Depend on both formId and loadForm

  const validateForm = () => {
    if (!form) return false;

    for (const field of form.fields) {
      if (field.required) {
        const value = formData[field.id];
        if (
          !value ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const handleInputChange = useCallback(
    (fieldId: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [fieldId]: value,
      }));

      if (onFormChange) {
        onFormChange(fieldId, value);
      }
    },
    [onFormChange]
  ); // Only depend on onFormChange

  // Separate useEffect to calculate progress when formData or form changes
  useEffect(() => {
    if (form) {
      const allFields = form.fields || [];
      const filledFields = allFields.filter((field) => {
        const fieldValue = formData[field.id];
        return fieldValue && fieldValue.toString().trim() !== "";
      });
      const progress =
        allFields.length > 0
          ? Math.round((filledFields.length / allFields.length) * 100)
          : 0;
      setFormProgress(progress);
    }
  }, [formData, form]); // Recalculate when formData or form changes

  // Function to transform flat family data to nested structure
  const transformFamilyDataToNested = (flatData: { [key: string]: any }) => {
    const nested: { [key: string]: any } = {};

    Object.entries(flatData).forEach(([key, entry]) => {
      if (!entry || typeof entry !== "object") return;

      const relationship = entry.relationship;

      if (relationship === "Father") {
        nested.parent1 = entry;
      } else if (relationship === "Mother") {
        nested.parent2 = entry;
      } else if (relationship === "Son" || relationship === "Daughter") {
        const childCount = Object.keys(nested).filter((k) =>
          k.startsWith("child")
        ).length;
        nested[`child${childCount + 1}`] = entry;
      } else if (relationship === "Brother" || relationship === "Sister") {
        const siblingCount = Object.keys(nested).filter((k) =>
          k.startsWith("sibling")
        ).length;
        nested[`sibling${siblingCount + 1}`] = entry;
      } else if (relationship === "Grandfather") {
        const grandparentCount = Object.keys(nested).filter((k) =>
          k.startsWith("grandparent")
        ).length;
        nested[`grandparent${grandparentCount + 1}`] = entry;
      } else if (relationship === "Grandmother") {
        const grandparentCount = Object.keys(nested).filter((k) =>
          k.startsWith("grandparent")
        ).length;
        nested[`grandparent${grandparentCount + 1}`] = entry;
      } else if (relationship === "Uncle") {
        const uncleCount = Object.keys(nested).filter((k) =>
          k.startsWith("uncle")
        ).length;
        nested[`uncle${uncleCount + 1}`] = entry;
      } else if (relationship === "Aunt") {
        const auntCount = Object.keys(nested).filter((k) =>
          k.startsWith("aunt")
        ).length;
        nested[`aunt${auntCount + 1}`] = entry;
      } else if (relationship === "Wife" || relationship === "Husband") {
        const spouseCount = Object.keys(nested).filter((k) =>
          k.startsWith("spouse")
        ).length;
        nested[`spouse${spouseCount + 1}`] = entry;
      } else if (relationship) {
        const otherCount = Object.keys(nested).filter((k) =>
          k.startsWith("other")
        ).length;
        nested[`other${otherCount + 1}`] = entry;
      }
    });

    return nested;
  };

  // Function to transform nested family data back to flat format
  const transformNestedFamilyDataToFlat = (nestedData: {
    [key: string]: any;
  }): { [key: string]: any } => {
    const flatData: { [key: string]: any } = {};
    let currentIndex = 1;

    // Convert nested structure back to numbered format
    Object.entries(nestedData).forEach(([key, entry]) => {
      if (entry && typeof entry === "object") {
        flatData[`familyDetails${currentIndex}`] = entry;
        currentIndex++;
      }
    });

    return flatData;
  };

  // Transform marriage data to nested structure
  const transformMarriageDataToNested = (marriageData: any) => {
    const nestedData: any = {};

    // Helper function to process date into multiple formats (old format)
    const processDate = (dateString: string) => {
      if (!dateString) return { full: "", year: "", month: "", date: "" };

      const date = new Date(dateString);
      if (isNaN(date.getTime()))
        return { full: dateString, year: "", month: "", date: "" };

      return {
        full: dateString,
        year: date.getFullYear().toString(),
        month: (date.getMonth() + 1).toString().padStart(2, "0"),
        date: date.getDate().toString().padStart(2, "0"),
      };
    };

    Object.keys(marriageData).forEach((key) => {
      if (key.startsWith("marriage")) {
        const entry = marriageData[key];
        const marriageNumber =
          Object.keys(nestedData).filter((k) => k.startsWith("marriage"))
            .length + 1;

        // Process marriage date
        const marriageDate = processDate(entry.dateOfMarriage);
        // Process spouse date of birth
        const spouseDob = processDate(entry.spouseDateOfBirth);

        nestedData[`marriage${marriageNumber}`] = {
          maritalStatus: entry.maritalStatus,
          dateOfMarriage: entry.dateOfMarriage,
          dateOfMarriageFull: marriageDate.full,
          dateOfMarriageYear: marriageDate.year,
          dateOfMarriageMonth: marriageDate.month,
          dateOfMarriageDate: marriageDate.date,
          spouse: {
            lastName: entry.spouseLastName,
            givenName: entry.spouseGivenName,
            dateOfBirth: entry.spouseDateOfBirth,
            dateOfBirthFull: spouseDob.full,
            dateOfBirthYear: spouseDob.year,
            dateOfBirthMonth: spouseDob.month,
            dateOfBirthDate: spouseDob.date,
            countryOfBirth: entry.spouseCountryOfBirth,
            presentAddress: entry.spousePresentAddress,
            occupation: entry.spouseOccupation,
          },
        };
      }
    });

    return nestedData;
  };

  // Transform nested marriage data back to flat structure for form display
  const transformMarriageDataToFlat = useCallback((marriageData: any) => {
    console.log("🔄 Transforming marriage data to flat:", marriageData);
    const flatData: any = {};

    Object.keys(marriageData).forEach((key) => {
      if (key.startsWith("marriage")) {
        const entry = marriageData[key];

        flatData[key] = {
          maritalStatus: entry.maritalStatus || "",
          dateOfMarriage: entry.dateOfMarriage || "",
          spouseLastName: entry.spouse?.lastName || "",
          spouseGivenName: entry.spouse?.givenName || "",
          spouseDateOfBirth: entry.spouse?.dateOfBirth || "",
          spouseCountryOfBirth: entry.spouse?.countryOfBirth || "",
          spousePresentAddress: entry.spouse?.presentAddress || "",
          spouseOccupation: entry.spouse?.occupation || "",
        };
      }
    });

    console.log("✅ Transformed marriage data:", flatData);
    return flatData;
  }, []);

  // Transform nested application country data back to flat structure
  const transformApplicationCountryToFlat = useCallback((data: any) => {
    console.log(
      "🔄 Transforming application country data to flat array:",
      data
    );
    if (!data || typeof data !== "object") return [];

    // Convert object with numeric keys to array
    const flatArray: any[] = [];
    Object.keys(data).forEach((key) => {
      if (typeof data[key] === "object" && data[key] !== null) {
        flatArray.push({
          id:
            data[key].id ||
            `application_country_${Date.now()}_${Math.random()}`,
          country: data[key].country || "",
          status: data[key].status || "",
          other: data[key].other || "",
          fromDate: data[key].fromDate || "",
          toDate: data[key].toDate || "",
        });
      }
    });
    console.log("✅ Transformed application country array:", flatArray);
    return flatArray;
  }, []);

  // Transform nested national identity data back to flat structure
  const transformNationalIdentityToFlat = useCallback((data: any) => {
    console.log("🔄 Transforming national identity data to flat array:", data);
    if (!data || typeof data !== "object") return [];

    // Convert object with numeric keys to array
    const flatArray: any[] = [];
    Object.keys(data).forEach((key) => {
      if (typeof data[key] === "object" && data[key] !== null) {
        flatArray.push({
          id: data[key].id || `national_id_${Date.now()}_${Math.random()}`,
          countryOfIssue: data[key].countryOfIssue || "",
          nationalIdNumber: data[key].nationalIdNumber || "",
          issueDate: data[key].issueDate || "",
          expiryDate: data[key].expiryDate || "",
        });
      }
    });
    console.log("✅ Transformed national identity array:", flatArray);
    return flatArray;
  }, []);

  // Date validation function
  const isValidDate = (dateString: string): boolean => {
    if (!dateString) return true; // Empty dates are valid (for optional fields)

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;

    // Check if the date string matches yyyy-mm-dd format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    // Check if the date is reasonable (not in the future for birth dates, etc.)
    const year = parseInt(dateString.split("-")[0]);
    const month = parseInt(dateString.split("-")[1]);
    const day = parseInt(dateString.split("-")[2]);

    // Basic range checks
    if (year < 1900 || year > new Date().getFullYear() + 10) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Check if the date actually exists (handles leap years, etc.)
    const testDate = new Date(year, month - 1, day);
    return (
      testDate.getFullYear() === year &&
      testDate.getMonth() === month - 1 &&
      testDate.getDate() === day
    );
  };

  // Education date validation function (yyyy-mm format)
  const isValidEducationDate = (dateString: string): boolean => {
    if (!dateString) return true; // Empty dates are valid (for optional fields)

    // Check if the date string matches yyyy-mm format
    const dateRegex = /^\d{4}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    const year = parseInt(dateString.split("-")[0]);
    const month = parseInt(dateString.split("-")[1]);

    // Basic range checks
    if (year < 1900 || year > new Date().getFullYear() + 10) return false;
    if (month < 1 || month > 12) return false;

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (!formId) {
        setError("Form ID is required");
        return;
      }

      console.log("Submitting form data:", JSON.stringify(formData, null, 2));

      // Get the code from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (!code) {
        setError("Access code is required");
        return;
      }

      // Transform family details data to nested structure before submission
      let transformedFormData = { ...formData };
      if (
        transformedFormData.familyDetails &&
        typeof transformedFormData.familyDetails === "object" &&
        transformedFormData.familyDetails !== null
      ) {
        transformedFormData.familyDetails = transformFamilyDataToNested(
          transformedFormData.familyDetails
        );
      }

      // Transform marriage data to nested structure before submission
      if (
        transformedFormData.marriageInformation &&
        typeof transformedFormData.marriageInformation === "object" &&
        transformedFormData.marriageInformation !== null
      ) {
        transformedFormData.marriageInformation = transformMarriageDataToNested(
          transformedFormData.marriageInformation
        );
      }

      // Process all date fields to include full, year, month, date format
      const processAllDates = (obj: any): any => {
        if (typeof obj !== "object" || obj === null) return obj;

        const processed: any = {};
        Object.keys(obj).forEach((key) => {
          const value = obj[key];

          // Skip fields that are already processed date components to prevent duplication
          // (e.g., skip "startDateFull", "startDateYear", etc.)
          if (
            key.endsWith("Full") ||
            key.endsWith("Year") ||
            key.endsWith("Month")
          ) {
            // Skip already-processed date fields
            return;
          }

          if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            // Full date format (yyyy-mm-dd)
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = value;
              processed[`${key}Full`] = value;
              processed[`${key}Year`] = date.getFullYear().toString();
              processed[`${key}Month`] = (date.getMonth() + 1)
                .toString()
                .padStart(2, "0");
              processed[`${key}Date`] = date
                .getDate()
                .toString()
                .padStart(2, "0");
            } else {
              processed[key] = value;
            }
          } else if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
            // Year-month format (yyyy-mm)
            const [year, month] = value.split("-");
            processed[key] = value;
            processed[`${key}Full`] = value;
            processed[`${key}Year`] = year;
            processed[`${key}Month`] = month;
          } else if (typeof value === "object" && value !== null) {
            // Recursively process nested objects
            processed[key] = processAllDates(value);
          } else {
            processed[key] = value;
          }
        });

        return processed;
      };

      // Apply date processing to the entire form data
      transformedFormData = processAllDates(transformedFormData);

      // Add form session ID for document linking
      if (formSessionId) {
        transformedFormData.formSessionId = formSessionId;
      }

      // Use the code-based submission instead of regular intake form submission
      await apiClient.submitFormByCode(code, transformedFormData);

      setSuccess(true);

      // Auto-redirect after 3 seconds
      setTimeout(() => {
        window.close();
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setError("Failed to submit form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: any) => {
    let value: any;
    if (field.type === "education-table") {
      value = formData[field.id] || {
        hasEducation: "No",
        educationEntries: [],
      };
    } else if (field.type === "work-history-table") {
      value = formData[field.id] || {};
    } else if (field.type === "family-members-table") {
      value = formData[field.id] || [];
    } else if (field.type === "travel-history-table") {
      value = formData[field.id] || {};
    } else if (field.type === "address-history-table") {
      value = formData[field.id] || [];
    } else if (field.type === "family-details-table") {
      value = formData[field.id] || {};
    } else if (field.type === "address-table") {
      value = formData[field.id] || {
        mailingAddress: {
          id: "mailing_1",
          poBox: "",
          streetNumber: "",
          streetName: "",
          aptNumber: "",
          city: "",
          province: "",
          country: "",
          postalCode: "",
          district: "",
        },
        residentialSameAsMailing: true,
        residentialAddressYes: "Y",
        residentialAddressNo: "N",
        residentialAddress: undefined,
      };
    } else if (field.type === "telephone-table") {
      value = formData[field.id] || [];
    } else if (field.type === "marriage-information-table") {
      value = formData[field.id] || {};
    } else if (field.type === "national-identity-table") {
      value = formData[field.id] || [];
    } else if (field.type === "passport-details-table") {
      value = formData[field.id] || [];
    } else if (field.type === "usa-pr-card-table") {
      value = formData[field.id] || [];
    } else if (field.type === "other-names-table") {
      value = formData[field.id] || [];
    } else if (field.type === "previous-countries-table") {
      value = formData[field.id] || [];
    } else if (field.type === "application-country-table") {
      value = formData[field.id] || [];
    } else if (field.type === "persons-to-visit-table") {
      value = formData[field.id] || [];
    } else {
      value = formData[field.id] || "";
    }

    const isRequired = field.required;

    switch (field.type) {
      case "group":
        return (
          <div className="field-group">
            {field.fields?.map((subField: any) => (
              <div key={subField.id} className="sub-field">
                <label className="sub-field-label">
                  {subField.label}
                  {subField.required && <span className="required">*</span>}
                </label>
                {renderField(subField)}
                {subField.required && (
                  <small className="field-help">This field is required</small>
                )}
              </div>
            ))}
          </div>
        );

      case "text":
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            className="form-input"
          />
        );

      case "email":
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder || "Enter your email"}
            required={isRequired}
            className="form-input"
          />
        );

      case "phone":
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder || "Enter your phone number"}
            required={isRequired}
            className="form-input"
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => {
              const dateValue = e.target.value;
              // Always update the field to preserve user input
              handleInputChange(field.id, dateValue);
            }}
            required={isRequired}
            className="form-input"
            placeholder="yyyy-mm-dd"
            pattern="\d{4}-\d{2}-\d{2}"
            min="1900-01-01"
            max="2099-12-31"
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              const value = input.value;

              // Prevent more than 4 digits in year
              if (value.length > 10) {
                input.value = value.substring(0, 10);
              }

              // Validate date format and days per month only for complete dates
              if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = value.split("-").map(Number);

                // Validate year range
                if (year < 1900 || year > 2099) {
                  input.setCustomValidity("Year must be between 1900 and 2099");
                  return;
                }

                // Validate days per month
                const daysInMonth = new Date(year, month, 0).getDate();
                if (day > daysInMonth) {
                  input.setCustomValidity(
                    `This month only has ${daysInMonth} days`
                  );
                } else {
                  input.setCustomValidity("");
                }
              } else {
                // Clear validation for incomplete dates
                input.setCustomValidity("");
              }
            }}
          />
        );

      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            rows={4}
            className="form-textarea"
          />
        );

      case "dropdown":
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={isRequired}
            className="form-select"
          >
            <option value="">Select an option</option>
            {field.options?.map((option: string, index: number) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div className="form-radio-group">
            {field.options?.map((option: string, index: number) => (
              <label key={index} className="form-radio-label">
                <input
                  type="radio"
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  required={isRequired}
                />
                {option}
              </label>
            ))}
          </div>
        );

      case "file":
        return (
          <IntakeFormDocumentUpload
            formSessionId={formSessionId || ""}
            onChange={(documents) => {
              setUploadedDocuments(documents);
              handleInputChange(field.id, documents);
            }}
            required={isRequired}
            maxFiles={5}
            acceptedTypes={[".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"]}
          />
        );

      case "education-table":
        return (
          <DynamicEducationTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "work-history-table":
        return (
          <DynamicWorkHistoryTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "family-members-table":
        // Skip rendering family members table (Family in Canada section)
        return null;

      case "travel-history-table":
        return (
          <DynamicTravelHistoryTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "address-history-table":
        // Skip rendering address history table
        return null;

      case "family-details-table":
        return (
          <DynamicFamilyDetailsTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "address-table":
        return (
          <DynamicAddressTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "telephone-table":
        return (
          <DynamicTelephoneTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "marriage-information-table":
        // Only show marriage table if marital status is selected and not the default option
        const maritalStatus = formData["maritalStatus"];
        const shouldShowMarriageTable =
          maritalStatus &&
          maritalStatus !== "" &&
          maritalStatus !== "Select an option";

        if (!shouldShowMarriageTable) {
          return null;
        }

        return (
          <DynamicMarriageInformationTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "national-identity-table":
        return (
          <DynamicNationalIdentityTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
            hasNationalId={formData.hasNationalId}
          />
        );

      case "passport-details-table":
        return (
          <DynamicPassportDetailsTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "usa-pr-card-table":
        return (
          <DynamicUsaPrCardTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
            hasUsaPrCard={formData.hasUsaPrCard}
          />
        );

      case "other-names-table":
        return (
          <DynamicOtherNamesTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "previous-countries-table":
        return (
          <DynamicPreviousCountriesTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "application-country-table":
        return (
          <DynamicApplicationCountryTable
            value={formData[field.id] || []}
            onChange={(value: any) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      case "persons-to-visit-table":
        return (
          <DynamicPersonsToVisitTable
            value={formData[field.id] || []}
            onChange={(value) => handleInputChange(field.id, value)}
            required={field.required}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={isRequired}
            className="form-input"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="public-form-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="public-form-container">
        <div className="error-container">
          <h2>Form Not Available</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => window.close()}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="public-form-container">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h2>Form Submitted Successfully!</h2>
          <p>
            Thank you for submitting your information. We'll contact you soon.
          </p>
          <p className="closing-note">
            This window will close automatically...
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  return (
    <div className="public-form-container">
      <div className="public-form">
        <div className="form-header">
          <h1>{form.title}</h1>
          <p className="form-description">
            Please fill out all required fields below. We'll review your
            information and contact you soon.
          </p>
          {lawyerInfo && (
            <div className="lawyer-info-header">
              <div className="lawyer-info-text">
                <h3>Representative Information:</h3>
                <p>
                  <strong>Name:</strong> {lawyerInfo.firstName}{" "}
                  {lawyerInfo.lastName}
                </p>
                <p>
                  <strong>Firm:</strong> {lawyerInfo.lawFirm}
                </p>
                <p>
                  <strong>Email:</strong> {lawyerInfo.email}
                </p>
                <p>
                  <strong>Phone:</strong> +{lawyerInfo.phoneCountryCode}{" "}
                  {lawyerInfo.phoneNumber}
                </p>
              </div>
              {(lawyerInfo.profileImageUrl || lawyerInfo.firmLogoUrl) && (
                <div className="lawyer-branding">
                  {lawyerInfo.profileImageUrl && (
                    <img
                      src={lawyerInfo.profileImageUrl}
                      alt="Representative profile"
                      className="lawyer-branding-avatar"
                    />
                  )}
                  {lawyerInfo.firmLogoUrl && (
                    <img
                      src={lawyerInfo.firmLogoUrl}
                      alt="Firm logo"
                      className="lawyer-branding-logo"
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {!lawyerInfo && (
            <div className="lawyer-info-header">
              <h3>Representative Information:</h3>
              <p style={{ color: "#666", fontStyle: "italic" }}>
                Representative information will be displayed here once
                available.
              </p>
              <p style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>
                Debug: Check browser console for loading details
              </p>
            </div>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Tab Navigation */}
        <div className="form-tabs">
          <button
            type="button"
            className={`tab-button ${activeTab === "form" ? "active" : ""}`}
            onClick={() => setActiveTab("form")}
          >
            {React.createElement(FaWpforms as any, {
              style: { fontSize: "14px", color: "#22c55e" },
            })}{" "}
            Form Fields
          </button>
          <button
            type="button"
            className={`tab-button ${
              activeTab === "documents" ? "active" : ""
            }`}
            onClick={() => setActiveTab("documents")}
          >
            {React.createElement(HiArrowUpTray as any, {
              style: { fontSize: "14px", color: "#22c55e" },
            })}{" "}
            Document Upload
          </button>
        </div>

        <form onSubmit={handleSubmit} className="intake-form">
          {/* Form Fields Tab */}
          {activeTab === "form" && (
            <div className="tab-content form-fields-tab">
              {form.fields &&
                form.fields.map((field) => {
                  // Skip rendering labels for hidden sections and specific fields
                  if (
                    field.type === "family-members-table" ||
                    field.type === "address-history-table" ||
                    field.id === "otherPhone" ||
                    field.id === "eyeColor" ||
                    field.id === "height"
                  ) {
                    return null;
                  }

                  return (
                    <div key={field.id} className="form-field">
                      <label className="field-label">
                        {field.label}
                        {field.required && <span className="required">*</span>}
                      </label>

                      {renderField(field)}

                      {field.required && (
                        <small className="field-help">
                          This field is required
                        </small>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* Document Upload Tab */}
          {activeTab === "documents" && (
            <div className="tab-content document-upload-tab">
              <div className="form-field document-upload-section">
                <label className="field-label">
                  Supporting Documents
                  <span className="field-help-text">
                    {" "}
                    (Optional - Upload any relevant documents)
                  </span>
                </label>
                <IntakeFormDocumentUpload
                  formSessionId={formSessionId || ""}
                  onChange={(documents) => {
                    setUploadedDocuments(documents);
                  }}
                  required={false}
                  maxFiles={10}
                  acceptedTypes={[
                    ".pdf",
                    ".jpg",
                    ".jpeg",
                    ".png",
                    ".doc",
                    ".docx",
                    ".txt",
                  ]}
                  uploaderEmail={
                    formData.email ||
                    formData.emailAddress ||
                    "client@example.com"
                  }
                />
                <small className="field-help">
                  You can upload documents such as passports, birth
                  certificates, marriage certificates, employment letters, or
                  any other relevant documentation for your case.
                </small>
              </div>
            </div>
          )}

          {/* Sticky Submit Bar */}
          <div className="sticky-submit-bar">
            <div className="progress-info">
              <span>Form Progress</span>
              {formProgress > 0 && (
                <div className="progress-indicator">
                  {formProgress}% Complete
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting || !areEssentialFieldsFilled()}
              className="sticky-submit-button"
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  {React.createElement(HiArrowUpTray as any, {
                    style: { fontSize: "16px", marginRight: "8px" },
                  })}
                  Submitting...
                </>
              ) : (
                <>
                  {React.createElement(HiPaperAirplane as any, {
                    style: { fontSize: "16px", marginRight: "8px" },
                  })}
                  Submit Form
                </>
              )}
            </button>
          </div>
        </form>

        <div className="form-footer">
          <p className="privacy-note">
            Your information is secure and will only be used for your case. We
            respect your privacy and will not share your information with third
            parties.
          </p>
        </div>
      </div>
    </div>
  );
};

export { PublicIntakeForm };
